import React, { useEffect, useMemo, useState } from "react";
import { GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Layout from "../../components/layout";
import ErrorPage404 from "../../components/404";
import PostBody from "../../components/post-body";
import BlogAuthorHero from "../../components/v2/blog/blog-author-hero";
import BlogPostHero from "../../components/v2/blog/blog-post-hero";
import BlogScrollNav from "../../components/v2/blog/blog-scroll-nav";
import BlogStickyCta from "../../components/v2/blog/blog-sticky-cta";
import BlogScrollTop from "../../components/v2/blog/blog-scroll-top";
import RelatedProductUpdates from "../../components/v2/blog/related-product-updates";
import {
  getProductUpdatesPosts,
  getProductUpdatesPostsBySlug,
} from "../../lib/api";
import { mwImage } from "../../lib/common";
import nextI18nextConfig from "../../next-i18next.config";
import "../../styles/scss/blog.module.scss";
import "../../styles/scss/customer.module.scss";
import "../../styles/scss/single-blog-v3.module.scss";

const parentPage = "product-updates";

function slugifyHeading(title: string) {
  return title
    .replace(/[^\w\s]/gi, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
}

export default function ProductUpdateSlug({ pageData, allProductUpdates }) {
  const router = useRouter();
  const post = pageData?.productUpdates?.edges?.[0]?.node;
  const [headings, setHeadings] = useState<{ id: string; title: string }[]>([]);

  const postContent = useMemo(
    () =>
      post?.content?.replace(
        /https:\/\/(middleware\.io\/backend|wpadmin\.middleware\.io)\/wp-content/g,
        "https://middleware.io/wp-content"
      ),
    [post?.content]
  );

  const featuredImage = mwImage(post?.featuredImage?.node?.sourceUrl || "");

  useEffect(() => {
    if (!post?.slug) return;

    const sourceElement = document.querySelector("#sticky-banner");
    const targetDiv = document.querySelector("#sticky");
    if (sourceElement && targetDiv) {
      targetDiv.appendChild(sourceElement);
    }
    return () => {
      const originalLocation = document.querySelector("#parent-banner");
      if (sourceElement && originalLocation) {
        originalLocation.appendChild(sourceElement);
      }
    };
  }, [post?.slug]);

  useEffect(() => {
    if (typeof document === "undefined" || !post?.slug || !postContent) return;

    const elements = document.querySelectorAll(".main-content .post-body h2");
    const nextHeadings: { id: string; title: string }[] = [];

    elements.forEach((item) => {
      if (item.tagName !== "H2") return;
      const title = item.textContent?.trim() || "";
      if (!title) return;
      const id = slugifyHeading(title);
      item.setAttribute("id", id);

      if (!item.querySelector(":scope > a.anchor-link")) {
        item.innerHTML = `
          <a aria-label="Link to section"
             class="anchor-link"
             href="#${id}">
             ${title}
          </a>
        `;
      }

      nextHeadings.push({ id, title });
    });

    setHeadings(nextHeadings);

    const Faqaccordion = document.querySelectorAll(".schema-faq-section");
    const faqHandler = (e: Event) => {
      (e.currentTarget as HTMLElement).classList.toggle("active");
    };
    Faqaccordion.forEach((child) => {
      child.addEventListener("click", faqHandler);
    });

    const setupAccordion = () => {
      const faq_accordion = document.getElementsByClassName("sc_fs_faq");
      if (faq_accordion.length > 0) {
        for (let i = 0; i < faq_accordion.length; i++) {
          if (i === 0) {
            faq_accordion[i].classList.add("active");
          }
          faq_accordion[i].addEventListener("click", (event) => {
            for (let j = 0; j < faq_accordion.length; j++) {
              faq_accordion[j].classList.remove("active");
            }
            (event.currentTarget as HTMLElement).classList.add("active");
          });
        }
      }
    };

    setupAccordion();
    const observer = new MutationObserver(() => {
      setupAccordion();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      Faqaccordion.forEach((child) => {
        child.removeEventListener("click", faqHandler);
      });
    };
  }, [postContent, post?.slug]);

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  if (!post?.slug) {
    return <ErrorPage404 />;
  }

  return (
    <>
      {featuredImage ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={featuredImage}
            fetchPriority="high"
          />
        </Head>
      ) : null}
      <Layout
        isHomePage={false}
        pageTitle={post?.title}
        ogImage={post?.featuredImage?.node?.sourceUrl}
        uri={post?.uri}
        metaTitle={post?.seo?.title}
        metaDesc={post?.seo?.metaDesc}
        metaRobotsNoindex={post?.seo?.metaRobotsNoindex}
        metaRobotsNofollow={post?.seo?.metaRobotsNofollow}
      >
        <div className="middle-wrapper site-page">
          <section className="single-blog-v3">
            <div className="blog-post-shell">
              <BlogPostHero
                post={post}
                backHref="/product-updates/"
                backLabel="BACK TO PRODUCT UPDATES"
                sharePathPrefix="/product-updates/"
                categories={[
                  {
                    name: "Product Updates",
                    slug: "product-updates",
                    href: "/product-updates/",
                  },
                ]}
                updatedAt={
                  post?.acf_productUpdate?.publishDate ||
                  post?.modified ||
                  post?.date
                }
              />
            </div>

            <div className="blog-article-layout">
              <BlogStickyCta />
              <div className="blog-article-column main-content">
                <PostBody content={postContent} />
                <div id="sticky" />
              </div>
              <BlogScrollNav headings={headings} />
              <BlogScrollTop />
              <BlogAuthorHero post={post} />
            </div>
          </section>

          <RelatedProductUpdates
            selectedSlug={post?.slug}
            data={allProductUpdates}
          />
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
}) => {
  const data = await getProductUpdatesPostsBySlug(params?.slug, parentPage);
  const post = data?.productUpdates?.edges?.[0]?.node;

  if (!post?.slug) {
    return { notFound: true };
  }

  const allProductUpdates = await getProductUpdatesPosts();

  return {
    props: {
      pageData: data,
      allProductUpdates: allProductUpdates ?? null,
      pageTitle: post?.seo?.title || null,
      description: post?.seo?.metaDesc || null,
      ogImage: post?.featuredImage?.node?.sourceUrl || null,
      uri: post?.uri || null,
      metaTitle: post?.seo?.title || null,
      metaDesc: post?.seo?.metaDesc || null,
      metaRobotsNoindex: post?.seo?.metaRobotsNoindex || null,
      metaRobotsNofollow: post?.seo?.metaRobotsNofollow || null,
      ...(await serverSideTranslations(
        locale ?? "en",
        ["common", "header", "footer"],
        nextI18nextConfig
      )),
    },
  };
};

export const getStaticPaths = async ({ locales }) => {
  const allPages = await getProductUpdatesPosts();
  const allLocales = locales || nextI18nextConfig.i18n.locales;

  const paths = allPages.edges.flatMap((item) => {
    return allLocales.map((locale) => ({
      params: { slug: item.node.slug },
      locale,
    }));
  });

  return {
    paths: paths,
    fallback: "blocking",
  };
};
